import {
    mkConfig,
    download,
    asString,
    generateCsv,
    ConfigOptions,
} from "export-to-csv";
import { SetRequired } from "type-fest";

type ExportToCsvOptions = SetRequired<
    Pick<
        ConfigOptions,
        | "filename"
        | "fieldSeparator"
        | "columnHeaders"
        | "title"
        | "showTitle"
        | "useKeysAsHeaders"
    >,
    "filename"
>;

// Spreadsheet apps evaluate cells starting with these as formulas.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;
// Plain numbers/amounts ("-5.00", "-$1,234.50", "+15551234567") and dash
// placeholders ("--") can't reference cells or call functions, so they are
// left as-is to keep them numeric in spreadsheets.
const INERT_VALUE = /^(?:-+|[+-]?\$?[\d,]*\.?\d+)$/;

const escapeFormula = <T>(value: T): T | string =>
    typeof value === "string" &&
    FORMULA_TRIGGER.test(value) &&
    !INERT_VALUE.test(value)
        ? `'${value}`
        : value;

type ColumnHeader = NonNullable<ConfigOptions["columnHeaders"]>[number];

const escapeHeader = (header: ColumnHeader): ColumnHeader =>
    typeof header === "string"
        ? escapeFormula(header)
        : {
              key: escapeFormula(header.key),
              displayLabel: escapeFormula(header.displayLabel),
          };

// `export-to-csv` writes the title line verbatim (unquoted), so a title
// containing the field separator or a newline would spill into extra cells
// that bypass the leading-character check. Emit it as one quoted cell.
const toTitleCell = (title: string) =>
    `"${escapeFormula(title).replace(/"/g, '""')}"`;

/**
 * Mimic the behavior of previous versions of `export-to-csv`,
 * functional api doesn't make much sense for our use case.
 * This class will also call into the `PartnerAppJsApi` to support
 * opening the csv files.
 */
export class ExportToCsv {
    #config: Required<ConfigOptions>;
    #download: ReturnType<typeof download>;
    #generateCsv: ReturnType<typeof generateCsv>;

    constructor(options: ExportToCsvOptions) {
        this.#config = mkConfig({
            ...options,
            ...(options.title !== undefined && {
                title: toTitleCell(options.title),
            }),
            ...(options.columnHeaders && {
                columnHeaders: options.columnHeaders.map(escapeHeader),
            }),
            useBom: false,
        });
        this.#download = download(this.#config);
        this.#generateCsv = generateCsv(this.#config);
    }

    generateCsv(data: any[]) {
        const csv = this.#generateCsv(
            data.map((row: Record<string, any>) =>
                Object.fromEntries(
                    // Keys are escaped too: with `useKeysAsHeaders` they become
                    // the header row, and `columnHeaders` keys are escaped to
                    // match in the constructor.
                    Object.entries(row).map(([key, value]) => [
                        escapeFormula(key),
                        escapeFormula(value),
                    ]),
                ),
            ),
        );
        this.#download(csv);
        window.PartnerAppJsApi?.openTextFile(
            asString(csv),
            "text/csv",
            `${this.#config.filename}.csv`,
        );
    }
}
